import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ClientProvider, useClient } from './context/ClientContext';
import Login from './components/auth/Login';
import AdminDashboard from './components/dashboard/AdminDashboard';
import ClientDashboard from './components/dashboard/ClientDashboard';
import PublicReviewForm from './components/reviews/PublicReviewForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ReviewDetail from './components/reviews/ReviewDetail';
import ReviewEdit from './components/reviews/ReviewEdit';
import ClientDetail from './components/clients/ClientDetail';
import UserDetail from './components/users/UserDetail';
import EmailFormPage from './components/dashboard/EmailFormPage';
import CsvEmailUploadPage from './components/dashboard/CsvEmailUploadPage';
import SettingsList from './components/settings/SettingsList';
import DashboardHeader from './components/dashboard/DashboardHeader';
import Footer from './components/dashboard/Footer';
import QuestionList from './components/questions/QuestionList';
import OptedListPage from './components/opted/OptedListPage';
import SentReviewEmailsPage from './components/emails/SentReviewEmailsPage';
import ClientUsersPage from './components/dashboard/ClientUsersPage';
import WidgetConfigPage from './components/widget/WidgetConfigPage';
import './App.css';

const Dashboard: React.FC = () => {
  const { user, isAdmin } = useAuth();

  if (isAdmin()) {
    return <AdminDashboard />;
  }

  return <ClientDashboard />;
};

const SettingsPage: React.FC = () => {
  const { selectedClientId } = useClient();

  return (
    <div className="dashboard">
      <DashboardHeader title="Settings" showNav={true} showEmailButton={true} />

      <div className="dashboard-content">
        <div className="settings-section">
          <SettingsList clientId={selectedClientId} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

const QuestionsPage: React.FC = () => {
  const { selectedClientId } = useClient();

  return (
    <div className="dashboard">
      <DashboardHeader title="Questions" showNav={true} showEmailButton={true} />

      <div className="dashboard-content">
        <div className="questions-section">
          <QuestionList clientId={selectedClientId} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

const WidgetPage: React.FC = () => {
  return (
    <div className="dashboard">
      <DashboardHeader title="Review Widget" showNav={true} showEmailButton={true} />

      <div className="dashboard-content">
        <div className="settings-section">
          <h2>Review Widget</h2>
          <WidgetConfigPage />
        </div>
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ClientProvider>
          <div className="App">
            <Routes>
              <Route path="/review/:uniqId" element={<PublicReviewForm />} />
              <Route path="/login" element={<Login />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/reviews/:id"
                element={
                  <ProtectedRoute>
                    <ReviewDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/reviews/:id/edit"
                element={
                  <ProtectedRoute>
                    <ReviewEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/clients/:id"
                element={
                  <ProtectedRoute>
                    <ClientDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/users"
                element={
                  <ProtectedRoute>
                    <ClientUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/users/:id"
                element={
                  <ProtectedRoute>
                    <UserDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/email"
                element={
                  <ProtectedRoute>
                    <EmailFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/email/upload-csv"
                element={
                  <ProtectedRoute>
                    <CsvEmailUploadPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/questions"
                element={
                  <ProtectedRoute>
                    <QuestionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/opted-emails"
                element={
                  <ProtectedRoute>
                    <OptedListPage type="email" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/opted-phones"
                element={
                  <ProtectedRoute>
                    <OptedListPage type="phone" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/sent-review-emails"
                element={
                  <ProtectedRoute>
                    <SentReviewEmailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/widget"
                element={
                  <ProtectedRoute>
                    <WidgetPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </ClientProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
